const pathWithoutNamespacePrefixes = function (path) {
  return String(path || '')
    .split('.')
    .map((part) => String(part || '').replace(/^[a-zA-Z_][\w-]*:/, ''))
    .join('.')
}

const pathWithoutLeadingScope = function (path) {
  return String(path || '').replace(/^(template|waylines)\./, '')
}

const pathWithoutNumericParts = function (path) {
  return String(path || '')
    .split('.')
    .filter((part) => !/^\d+$/.test(String(part || '')))
    .join('.')
}

const getPathLeafField = function (path) {
  const parts = String(path || '').split('.').filter((part) => !!String(part || '').trim())
  for (let i = parts.length - 1; i >= 0; i--) {
    if (/^\d+$/.test(parts[i])) continue
    if (parts[i] === '@param' || parts[i] === '_text') continue
    return parts[i]
  }
  return ''
}

const getCanonicalLeafFieldFromPath = function (path) {
  return getPathLeafField(
    pathWithoutNumericParts(
      pathWithoutLeadingScope(
        pathWithoutNamespacePrefixes(path)
      )
    )
  )
}

const WPML_NS_DEFAULT = 'http://www.dji.com/wpmz/1.0.6'

const findDirectChildByTagSuffix = function (parent, suffix) {
  const safeSuffix = String(suffix || '').trim()
  if (!parent || !safeSuffix) return null
  return Array.from(parent.children || []).find((child) => {
    const tag = String(child.tagName || '')
    return tag === safeSuffix || tag.endsWith(':' + safeSuffix)
  }) || null
}

const toUnixMs = function (rawValue) {
  const n = Number(rawValue)
  if (!Number.isFinite(n)) return null
  return Math.trunc(n)
}

const formatUnixMsForDisplay = function (rawValue, displayFormat, momentLib) {
  const unixMs = toUnixMs(rawValue)
  if (!Number.isFinite(unixMs) || unixMs <= 0) return ''
  const m = (typeof momentLib === 'function') ? momentLib(unixMs) : null
  if (!m || typeof m.isValid !== 'function' || !m.isValid()) return ''
  const fmt = String(displayFormat || 'YYYY-MM-DD HH:mm:ss')
  return m.format(fmt)
}

const parseDisplayTimestampToUnixMs = function (rawText, displayFormat, momentLib) {
  const text = String(rawText || '').trim()
  if (!text) return null
  if (typeof momentLib !== 'function') return null
  const fmt = String(displayFormat || 'YYYY-MM-DD HH:mm:ss')
  const m = momentLib(text, fmt, true)
  if (!m || typeof m.isValid !== 'function' || !m.isValid()) return null
  return m.valueOf()
}

const upsertDocumentWpmlTimestamp = function (documentEl, tagSuffix, unixMs, options) {
  const opts = Object.assign({
    createIfMissing: false,
    namespaceUri: WPML_NS_DEFAULT
  }, options || {})
  if (!documentEl) return false
  const safeSuffix = String(tagSuffix || '').trim()
  if (!safeSuffix) return false
  const textValue = String(Math.max(0, Math.round(Number(unixMs) || 0)))
  let el = findDirectChildByTagSuffix(documentEl, safeSuffix)
  if (!el && opts.createIfMissing) {
    try {
      el = documentEl.ownerDocument.createElementNS(String(opts.namespaceUri || WPML_NS_DEFAULT), 'wpml:' + safeSuffix)
    } catch (_error) {
      el = documentEl.ownerDocument.createElement('wpml:' + safeSuffix)
    }
    if (!el) return false
    if (documentEl.firstChild) {
      documentEl.insertBefore(el, documentEl.firstChild)
    } else {
      documentEl.appendChild(el)
    }
  }
  if (!el) return false
  if (String(el.textContent || '').trim() === textValue) return false
  el.textContent = textValue
  return true
}

const readDocumentWpmlTimestampMs = function (documentEl, tagSuffix) {
  if (!documentEl) return null
  const safeSuffix = String(tagSuffix || '').trim()
  if (!safeSuffix) return null
  const el = findDirectChildByTagSuffix(documentEl, safeSuffix)
  if (!el) return null
  const raw = String(el.textContent || '').trim()
  if (!raw) return null
  const n = Number(raw)
  if (!Number.isFinite(n)) return null
  return Math.trunc(n)
}

const getMissionCentroidLatLng = function (parsedMission) {
  const points = (parsedMission && Array.isArray(parsedMission.waypoints)) ? parsedMission.waypoints : []
  let latSum = 0
  let lngSum = 0
  let count = 0
  points.forEach((pt) => {
    const latlng = pt && Array.isArray(pt.latlng) ? pt.latlng : null
    const lat = latlng ? Number(latlng[0]) : NaN
    const lng = latlng ? Number(latlng[1]) : NaN
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
    latSum += lat
    lngSum += lng
    count += 1
  })
  if (!count) return null
  return { lat: latSum / count, lng: lngSum / count }
}

const normalizeMissionBaseName = function (name) {
  const text = String(name || '').trim()
  if (!text) return 'new_mission'
  const safe = text
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
  return safe || 'new_mission'
}

const getMissionFileExtension = function (missionType) {
  return (missionType === 'kmz') ? '.kmz' : '.kml'
}

const normalizeMissionFileName = function (raw, missionType) {
  const missionExt = getMissionFileExtension(missionType)
  const input = String(raw || '').trim()
  const withoutExt = input.replace(/\.(kml|kmz)$/i, '').trim()
  const safeBase = String(withoutExt || 'mission')
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
  return (safeBase || 'mission') + missionExt
}

const fetchNearestSettlementName = async function (lat, lng) {
  try {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
    const controller = window.AbortController ? new AbortController() : null
    let timeoutId = null
    if (controller) {
      timeoutId = setTimeout(function () {
        try { controller.abort() } catch (_error) {}
      }, 2500)
    }
    const url = 'https://nominatim.openstreetmap.org/reverse?format=jsonv2&accept-language=hu&zoom=10&lat=' + encodeURIComponent(lat) + '&lon=' + encodeURIComponent(lng)
    const response = await fetch(url, {
      method: 'GET',
      signal: controller ? controller.signal : undefined,
      headers: { Accept: 'application/json' }
    })
    if (timeoutId) clearTimeout(timeoutId)
    if (!response.ok) return null
    const data = await response.json()
    const address = (data && typeof data.address === 'object') ? data.address : {}
    const settlement = address.city || address.town || address.village || address.municipality || address.county || null
    return settlement ? String(settlement).trim() : null
  } catch (_error) {
    return null
  }
}

const buildLocationBasedMissionBaseName = async function (parsedMission) {
  const center = getMissionCentroidLatLng(parsedMission)
  if (!center) return 'new_mission'
  const settlement = await fetchNearestSettlementName(center.lat, center.lng)
  return normalizeMissionBaseName(settlement || 'new_mission')
}

const IMPORT_COLOR_ARRAY = [
  '#FF6633', '#FF33FF', '#00B3E6', '#3366E6', '#99FF99', '#80B300', '#E6B3B3',
  '#66991A', '#CCFF1A', '#E6331A', '#66994D', '#4D8000', '#66664D', '#E666FF',
  '#1AB399', '#33991A', '#B3B31A', '#4D8066', '#E6FF80', '#999933', '#CCCC00',
  '#4D80CC', '#E64D66', '#FF4D4D', '#6666FF'
]

const POLYLINE_COLOR_ARRAY_VIBRANT = [
  '#FF0000',
  '#0000FF',
  '#FF00FF',
  '#FFA500',
  '#800080',
  '#FF1493'
]

const arrayStepper = function (a, i) {
  const list = Array.isArray(a) ? a : []
  if (!list.length) return '#666'
  const idx = Number.isFinite(Number(i)) ? Number(i) : 0
  const safeIdx = ((idx % list.length) + list.length) % list.length
  return list[safeIdx]
}

const normalizeTokens = function (type) {
  const raw = Array.isArray(type) ? type : [type]
  return raw
    .map(function (token) { return String(token || '').trim().toLowerCase() })
    .filter(Boolean)
}

const buildLayerStyle = function (type, row) {
  const style = {
    color: 'rgba(0,0,0,1.0)',
    fillColor: 'rgba(100,100,100,1.0)',
    weight: 1,
    dashOffset: 0,
    opacity: 0.6,
    fillOpacity: 0.2
  }

  const tokens = normalizeTokens(type)
  const num = tokens.length ? tokens[tokens.length - 1] : 0
  const tokenSet = new Set(tokens)

  if (tokenSet.has('import')) {
    style.color = arrayStepper(IMPORT_COLOR_ARRAY, num)
  }
  if (tokenSet.has('polyline')) {
    style.weight = 8
    style.color = arrayStepper(POLYLINE_COLOR_ARRAY_VIBRANT, num)
  }
  if (tokenSet.has('airspace')) {
    style.fillColor = arrayStepper(IMPORT_COLOR_ARRAY, num)
    style.dashArray = '4, 4'
  }
  if (tokenSet.has('airport')) {
    const typeRadius = {
      'I.': 3000,
      'II.': 3000,
      'III.': 3000,
      'IV.': 3000,
      'V.': 3000,
      'VI.': 3000,
      '': 750
    }
    const typeColor = {
      'I.': 'red',
      'II.': 'red',
      'III.': 'orange',
      'IV.': 'purple',
      'V.': 'pink',
      'VI.': 'magenta',
      '': 'magenta'
    }
    const airportClass = String((row && row.class) || '')
    style.radius = typeRadius[airportClass] || typeRadius['']
    style.color = typeColor[airportClass] || typeColor['']
    style.fillColor = typeColor[airportClass] || typeColor['']
  }
  if (tokenSet.has('military')) {
    style.fillColor = 'MediumSeaGreen'
  }
  if (tokenSet.has('uas')) {
    style.fillColor = 'orange'
  }
  if (tokenSet.has('uas') && tokenSet.has('military')) {
    style.fillColor = 'red'
  }
  if (tokenSet.has('glider')) {
    style.fillColor = 'DodgerBlue'
  }
  if (tokenSet.has('prohibited')) {
    style.fillColor = 'yellow'
    style.dashArray = '4, 4'
  }
  if (tokenSet.has('restricted')) {
    style.fillColor = 'black'
    style.dashArray = '4, 4'
  }
  if (tokenSet.has('danger')) {
    style.fillColor = 'purple'
    style.dashArray = '4, 4'
  }
  if (tokenSet.has('nature')) {
    style.color = 'red'
    style.fillColor = 'DarkGreen'
    style.dashArray = '4, 4'
  }

  return style
}

export {
  pathWithoutNamespacePrefixes,
  pathWithoutLeadingScope,
  pathWithoutNumericParts,
  getPathLeafField,
  getCanonicalLeafFieldFromPath,
  WPML_NS_DEFAULT,
  toUnixMs,
  formatUnixMsForDisplay,
  parseDisplayTimestampToUnixMs,
  upsertDocumentWpmlTimestamp,
  readDocumentWpmlTimestampMs,
  getMissionCentroidLatLng,
  normalizeMissionBaseName,
  getMissionFileExtension,
  normalizeMissionFileName,
  fetchNearestSettlementName,
  buildLocationBasedMissionBaseName,
  buildLayerStyle,
  normalizeTokens
}
